import { TableCell, TableRow } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { parseUserNode, useInvitationByUser } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getUser } from '/@/store/session/selectors';
import type { Grower } from '/@/types/growers';
import { reverse } from '/@/utils/router';
import GrowersListActions from '../../../../growers-list-actions';
import GrowerNameCell from '../../../components/grower-name-cell';
import YieldByCropCell from '../../../components/yield-by-crop-cell';

const ReportsGrowerRow = ({
    grower,
    cropFilter,
    year,
    onGrowerDeleteSuccess,
}: {
    grower: Grower;
    cropFilter: string;
    year: number;
    onGrowerDeleteSuccess: (growerName: string) => void;
}) => {
    const navigate = useNavigate();
    const units = useUnitProvider();
    const user = useSelector(getUser);

    // Transform API data to component format
    const transformGrowerData = useCallback(
        (grower: Grower) => {
            const parsedUserNode = parseUserNode(grower, user);
            const fields = grower.fields || {};
            const totalFields = fields.count || 0;

            // Generate suggestions

            const fieldviewConnected = grower.user?.authProviders?.includes('fieldview');

            return {
                id: grower.id,
                name: parsedUserNode.isSelf ? _('Mi cuenta') : parsedUserNode.name,
                location: parsedUserNode.location,
                totalFields,
                totalArea: convertAreaFromM2(grower?.fields.totalArea || 0, units).toString(),
                fieldviewConnected: fieldviewConnected,
                hasAssignedUser: grower.user !== null,
                totalReports: grower.cropMonitoringResultStatistics.count,
                phone: parsedUserNode.phone,
                email: parsedUserNode.email,
                isSelf: parsedUserNode.isSelf,
            };
        },
        [units, user]
    );

    const transformedGrower = useMemo(() => transformGrowerData(grower), [grower, transformGrowerData]);

    const { invitationStatus, invitationSendedMoreThan7DaysAgo } = useInvitationByUser({
        userNodeId: grower.id,
    });

    return (
        <TableRow
            key={transformedGrower.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
                navigate(
                    reverse('Growers.detail', {
                        params: {
                            growerNodeId: transformedGrower.id,
                        },
                        qs: {
                            tab: 'past-reports',
                        },
                    })
                );
            }}
        >
            <TableCell width='20%'>
                <GrowerNameCell
                    name={transformedGrower.name}
                    location={transformedGrower.location}
                    hasAssignedUser={transformedGrower.hasAssignedUser}
                    invitationStatus={invitationStatus}
                />
            </TableCell>
            <TableCell width='20%'>
                <TableTemplateCell
                    title={_('{{count}} Lote', { count: transformedGrower.totalFields })}
                    subtitle={_('Superficie total: {{value}}', { value: transformedGrower.totalArea })}
                />
            </TableCell>
            {transformedGrower.totalReports > 0 ? (
                <>
                    <TableCell width='20%'>
                        <TableTemplateCell title={_('{{count}} Reporte', { count: transformedGrower.totalReports })} />
                    </TableCell>
                    <TableCell width='30%'>
                        <YieldByCropCell grower={grower} cropFilter={cropFilter} year={year} />
                    </TableCell>
                </>
            ) : (
                <TableCell width='50%' colSpan={2}>
                    <TableTemplateCell title={_('Sin reportes generados')} />
                </TableCell>
            )}
            <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                <GrowersListActions
                    growerNodeId={transformedGrower.id}
                    editable={!transformedGrower.isSelf}
                    growerPhone={transformedGrower.phone || ''}
                    growerName={transformedGrower.name}
                    tab='past-reports'
                    onGrowerDeleteSuccess={onGrowerDeleteSuccess}
                    deletable={!transformedGrower.hasAssignedUser}
                />
            </TableCell>
        </TableRow>
    );
};

export default ReportsGrowerRow;
